import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import saveSendMessage from 'parog-web/components/chat/utils/save-send-message';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import type { Payload } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import ParamedicalCarePrescribedJournalEvent from 'parog-web/models/paramedical-care-prescribed-journal-event';
import type AnalyticsService from 'parog-web/services/analytics';
import { sortBy } from 'parog-web/utils/sort';

interface Model extends Payload {
  willSendMessage: boolean;
  chatMessage: string;
}

export default class ParamedicalCareEditionFormComponent extends CrudJournalEventBaseComponent {
  static override forcDisplayName = 'Prescription de soins paramédicaux';
  static override fullComponentId =
    'prescription/paramedical-care-edition-form';
  static override journalEventClass = ParamedicalCarePrescribedJournalEvent;

  @service declare analytics: AnalyticsService;
  @service declare router: RouterService;

  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  async _openPrescriptionInNewTab(): Promise<void> {
    // The last JE created of the given type should be the JE saved in the form.
    const journalEvent = this.store
      .peekAll('paramedical-care-prescribed-journal-event')
      // @ts-expect-error the @types/ember-data types are wrong.
      .toSorted(sortBy('occurredAt', 'desc'))
      .at(-1);

    // Open the PDF prescription in a new tab
    if (journalEvent) {
      this.router.transitionTo(
        'medics-web.protected.patients.patient.prescriptions.paramedical-care-display',
        journalEvent.patient.id,
        journalEvent.id
      );
    } else {
      throw new Error(
        'Impossible to open the PDF prescription because there is no JournalEvent found corresponding to the type : paramedical-care-prescribed-journal-event'
      );
    }
  }

  override afterCompleted = (): Transition =>
    this.router.transitionTo('medics-web.protected.patients.patient.dashboard');

  override async save(): Promise<void> {
    const model = await super.save();

    if (this.patient.authorizesAppUsage) {
      await saveSendMessage(model as Model, {
        store: this.store,
        patient: this.args.patient,
        analytics: this.analytics
      });

      await this.store
        .createRecord('patient-chat-message-sent-journal-event', {
          occurredAt: new Date(),
          triggeringJournalEvent: model,
          patient: model.patient,
          body: 'Nouvelle ordonnance reçue'
        })
        .save();
    }

    await this.draft.onDidSave();

    await this._openPrescriptionInNewTab();
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <div class='field'>
          <Form.TextArea
            @labelName="Soins en lien avec l'ALD"
            @propertyName='careAld'
            @placeholder='Pensez à préciser lorsque nécessaire la nature des soins attendus, la fréquence, la durée, la date de début des soins/séances. Préciser le lieux d’exécution lorsque pertinent et si ces derniers doivent être effectués le dimanche / les jours fériés'
          />
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName="Soins n'étant pas en lien avec l'ALD"
            @propertyName='careNonAld'
            @placeholder='Pensez à préciser lorsque nécessaire la nature des soins attendus, la fréquence, la durée, la date de début des soins/séances. Préciser le lieux d’exécution lorsque pertinent et si ces derniers doivent être effectués le dimanche / les jours fériés'
          />
        </div>

        {{#if @patient.authorizesAppUsage}}
          <div class='field'>
            <Form.SendMessage
              @willSendMessage={{true}}
              @defaultChatMessage='Bonjour,
      Je viens de vous éditer une nouvelle ordonnance. Elle est jointe à ce message.
      Bonne journée.'
            />
          </div>
        {{/if}}
      </:content>
    </Provider>
  </template>
}
